import React, { useCallback, useEffect, useState, memo, useMemo } from 'react';
import './calculation-config.css';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { CalculationConfigValue, CalculationConfigProps, CalculationPath, SourceRelationOption, SourceRelationValue, RootState } from '../types';
import CustomTooltip from '../../../Tooltip/CustomTooltip';
import ConfirmPopup from '../../../ModalPopup/confirm-popup';
import LibraryTitleInput from '../../library-title-input';
import { toasterMsg } from '../../../Toaster/Toaster';
import { fetchCustomColumnList, fetchCustomSectionBomTypeAction, getAllMeasurment, getSampleTrackingDates } from 'Pages/RangePlan/CustomSections/store/CustomSections.action';
import { getCustomAttributeDetail, getMainSampleDetails, getOtherSamplesDetails, getPlacementDetails } from 'Pages/SystemMasters/Store/SystemMasters.action';
import { FormulaDisplay, FormulaButtons, PathConfig } from './calculation-components';
import {
  emptyPath,
  emptyRelation,
  isDeepEqual,
  migratePath,
  buildBreadcrumb,
  buildDynamicBomSelection,
  buildManualCustomColumns,
  buildSampleTypeFromStore,
  setPlacementForLocation,
  setSubLocationArr,
  setOptionsForAttribute,
  setOptionsForOutput,
  setOptionsForBomOutput,
  setAttrinuteToValue,
  isPathComplete,
  checkPathHasStringType,
  locationOptions,
  buildOption,
} from './calculation-config.utils';
import {
  calculateBracketCounter,
  checkFormulaHasPercentage,
  checkFormulaHasRoot,
  checkSqAtLast,
  checkPiAtLast,
  checkPiSquareAtLast,
  checkNumberSquareAtLast,
  checkDisableNumbers,
  checkDisableSpecials,
  hasStringTypePath,
  hasPathInFormula,
  validateFormula,
  type FormulaValidationResult,
} from './calculation-config.formula';
import { prouctCustmSectionAttributeList } from '../../../helper';
import { listModules, getModuleById, getLibraryTabDetails } from 'services/custom-module.services';
import { SaveIcon } from '../../../../bombiix-icon';
import { NewCheckbox } from '../../../PIM/common/NewCheckbox';
import { NewInput } from '../../../PIM/common/NewInput';

const EXCLUDED_CM_CONTROL_TYPES = ['table', 'calculation', 'image', 'canvas', 'source'];

type RawModuleTab = { _id?: { toString(): string } | string; title?: string; label?: string };
type RawSection = { _id?: { toString(): string } | string; title?: string; label?: string; content?: RawSubSection[] };
type RawSubSection = { _id?: { toString(): string } | string; title?: string; label?: string; content?: RawControl[] };
type RawControl = { _id?: { toString(): string } | string; title?: string; label?: string; type?: string };

type ListModulesResponse = { data?: { modules?: Array<{ _id?: { toString(): string } | string; name?: string }> } };
type ModuleByIdResponse = { data?: { module?: { page?: RawModuleTab[] } } };
type ModuleTabDetailsResponse = { data?: { tab?: { sections?: RawSection[] } } };

const deriveAllTabsFromModule = (modulePage: RawModuleTab[]): SourceRelationOption[] => {
  return (modulePage || []).map((tab) => ({
    label: tab.title || tab.label || '',
    name: tab._id?.toString() || '',
    _id: tab._id?.toString() || '',
  }));
};

const deriveSectionsFromTab = (tabSections: RawSection[]): SourceRelationOption[] => {
  return (tabSections || []).map((sec) => ({
    label: sec.title || sec.label || '',
    name: sec._id?.toString() || '',
    _id: sec._id?.toString() || '',
  }));
};

const deriveSubSectionsFromSection = (sectionContent: RawSubSection[]): SourceRelationOption[] => {
  return (sectionContent || []).map((sub) => ({
    label: sub.title || sub.label || 'Subsection',
    name: sub._id?.toString() || '',
    _id: sub._id?.toString() || '',
  }));
};

const deriveControlsFromSubSection = (subSectionContent: RawControl[]): SourceRelationOption[] => {
  const filtered = (subSectionContent || []).filter((ctrl) => {
    const controlType = ctrl.type?.toLowerCase() || '';
    return !EXCLUDED_CM_CONTROL_TYPES.includes(controlType);
  });
  return filtered.map((ctrl, index) => {
    const title = ctrl.title || ctrl.label || '';
    const label = title || `Field ${index + 1} (${ctrl.type || 'unknown'})`;
    return {
      label,
      name: ctrl._id?.toString() || '',
      _id: ctrl._id?.toString() || '',
    };
  });
};

const CalculationConfig: React.FC<CalculationConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
  hideTitle = false,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const showWidthRow = onWidthChange != null;
  const dispatch = useDispatch();
  const reducer = useSelector((state: RootState) => state);

  const {
    customSectionData: { bomTypes, sampleTrackingDates, customColumnList, currSectionList },
    SystemMastersDetails: { placement_master, subBrandAccess, customAttributeDetail, mainSampleList, otherSamplesList },
  } = reducer;

  const [dynamicBomSelection, setDynamicBomSelection] = useState<SourceRelationOption[]>([]);
  const [manualCustomColumns, setManualCustomColumns] = useState<SourceRelationOption[]>([]);
  const [dynamicLocationOptions, setDynamicLocationOptions] = useState<SourceRelationOption[]>(locationOptions);

  const [localValue, setLocalValue] = useState<CalculationConfigValue>(() => {
    const normalizedPaths = value.paths && Array.isArray(value.paths) && value.paths.length > 0 ? value.paths.map(migratePath) : [];
    return {
      title: value.title ?? '',
      required: !!value.required,
      isVisibleToSupplier: value.isVisibleToSupplier ?? true,
      paths: normalizedPaths,
      formula: value.formula ?? '',
    };
  });

  const [libraryTitle, setLibraryTitle] = useState<string>('');
  const [formulaInput, setFormulaInput] = useState<string>(value.formula ?? '');
  const [editingPathIndex, setEditingPathIndex] = useState<number | null>(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showClearConfirm, setShowClearConfirm] = useState<boolean>(false);
  const [formulaValidation, setFormulaValidation] = useState<FormulaValidationResult>({ isValid: true });
  const [rootAtLast, setRootAtLast] = useState<boolean>(false);

  const [cmModuleList, setCmModuleList] = useState<SourceRelationOption[]>([]);
  const [cmTabLists, setCmTabLists] = useState<Record<number, SourceRelationOption[]>>({});
  const [cmSectionLists, setCmSectionLists] = useState<Record<number, SourceRelationOption[]>>({});
  const [cmSubSectionLists, setCmSubSectionLists] = useState<Record<number, SourceRelationOption[]>>({});
  const [cmControlLists, setCmControlLists] = useState<Record<number, SourceRelationOption[]>>({});

  const isUserEditingRef = React.useRef(false);
  const cmTabContentCacheRef = React.useRef<Record<string, RawSection[]>>({});
  const cascadePreloadDoneRef = React.useRef(false);

  useEffect(() => {
    if (formulaInput !== localValue.formula && !isUserEditingRef.current) {
      setLocalValue((prev) => ({ ...prev, formula: formulaInput }));
    }
  }, [formulaInput, localValue.formula]);

  useEffect(() => {
    if (isUserEditingRef.current) {
      return;
    }
    const normalizedPaths = value.paths && Array.isArray(value.paths) && value.paths.length > 0 ? value.paths.map(migratePath) : [];
    const next: CalculationConfigValue = {
      title: value.title ?? '',
      required: !!value.required,
      isVisibleToSupplier: value.isVisibleToSupplier ?? true,
      paths: normalizedPaths,
      formula: value.formula ?? '',
    };
    setLocalValue((prev) => {
      if (isDeepEqual(next, prev)) {
        return prev;
      }
      return next;
    });
    if (value.formula !== undefined && value.formula !== formulaInput) {
      setFormulaInput(value.formula);
    }
  }, [value, formulaInput]);

  useEffect(() => {
    const userId = reducer?.UserDetails?.userInfo?.userId;
    (dispatch as unknown as (a: unknown) => unknown)(fetchCustomSectionBomTypeAction({ isAllRecord: true }));
    if (userId) {
      (dispatch as unknown as (a: unknown) => unknown)(getPlacementDetails({ loggedInUserId: userId, isActive: true }));
      (dispatch as unknown as (a: unknown) => unknown)(getMainSampleDetails({ loggedInUserId: userId }));
      (dispatch as unknown as (a: unknown) => unknown)(getOtherSamplesDetails({ loggedInUserId: userId }));
    }
    (dispatch as unknown as (a: unknown) => unknown)(getAllMeasurment());
    (dispatch as unknown as (a: unknown) => unknown)(getSampleTrackingDates());
    (dispatch as unknown as (a: unknown) => unknown)(getCustomAttributeDetail({ isActive: true }));
  }, [dispatch, reducer?.UserDetails?.userInfo?.userId]);

  useEffect(() => {
    const merged = buildDynamicBomSelection(bomTypes);
    setDynamicBomSelection(merged);
  }, [bomTypes]);

  useEffect(() => {
    const manualCols = buildManualCustomColumns(customColumnList ?? undefined);
    setManualCustomColumns(manualCols);
  }, [customColumnList]);

  useEffect(() => {
    const allColumns: any[] = [];
    if (currSectionList && Array.isArray(currSectionList) && currSectionList.length > 0) {
      currSectionList.forEach((sectionElement: any) => {
        const checkManualExist = sectionElement?.columns?.find((vl: any) => vl?.columnType === 'manual');
        if (checkManualExist) {
          allColumns.push(sectionElement);
        }
      });
      const manualColumns = allColumns.map((cEle: any) => ({
        label: cEle?.name,
        name: cEle?._id,
        _id: 12,
      }));
      setDynamicLocationOptions([...locationOptions, ...manualColumns]);
    } else {
      setDynamicLocationOptions(locationOptions);
    }
  }, [currSectionList]);

  const sampleTypeFromStore: SourceRelationOption[] = useMemo(() => {
    return buildSampleTypeFromStore(mainSampleList, otherSamplesList);
  }, [mainSampleList, otherSamplesList]);

  const loadModuleTabsForPath = useCallback((moduleId: string, onLoaded: (tabs: RawModuleTab[]) => void) => {
    getModuleById(moduleId)
      .then((res: unknown) => {
        const tabs = (res as ModuleByIdResponse)?.data?.module?.page ?? [];
        onLoaded(tabs);
      })
      .catch(() => {});
  }, []);

  const loadTabSectionsForPath = useCallback(
    (moduleId: string, tabId: string, onLoaded: (sections: RawSection[]) => void) => {
      const cacheKey = `${moduleId}:${tabId}`;
      const cached = cmTabContentCacheRef.current[cacheKey];
      if (cached) {
        onLoaded(cached);
        return;
      }
      getLibraryTabDetails(moduleId, tabId)
        .then((res: unknown) => {
          const sections = (res as ModuleTabDetailsResponse)?.data?.tab?.sections ?? [];
          cmTabContentCacheRef.current[cacheKey] = sections;
          onLoaded(sections);
        })
        .catch(() => {});
    },
    [],
  );

  const handleLibrarySave = useCallback((title: string) => {
    setLibraryTitle('');
    setLocalValue((prev) => ({ ...prev, title }));
  }, []);

  const handleLibrarySelect = useCallback((controlTypeData: any) => {
    if (controlTypeData?.content?.content) {
      isUserEditingRef.current = false;
      const content = controlTypeData.content.content;
      const newFormula = content.formula ?? '';
      setFormulaInput(newFormula);
      setLocalValue({
        title: controlTypeData.title ?? '',
        required: content.required ?? false,
        isVisibleToSupplier: content.isVisibleToSupplier ?? true,
        paths: content.paths && content.paths.length > 0 ? content.paths.map(migratePath) : [],
        formula: newFormula,
      });
    }
  }, []);

  const toggleRequired = useCallback(() => setLocalValue((prev) => ({ ...prev, required: !prev.required })), []);

  const updateCalculationName = useCallback((next: string) => {
    isUserEditingRef.current = true;
    setLocalValue((prev) => ({ ...prev, title: next }));
  }, []);

  const toggleSupplierVisible = useCallback(() => setLocalValue((prev) => ({ ...prev, isVisibleToSupplier: !prev.isVisibleToSupplier })), []);

  const checkPathHasStringTypeCallback = useCallback(
    (path: CalculationPath): boolean => {
      return checkPathHasStringType(path, manualCustomColumns, customAttributeDetail, sampleTrackingDates, subBrandAccess);
    },
    [manualCustomColumns, customAttributeDetail, sampleTrackingDates, subBrandAccess],
  );

  const hasStringTypePathValue = useMemo(() => {
    return hasStringTypePath(formulaInput, localValue.paths, checkPathHasStringTypeCallback);
  }, [formulaInput, localValue.paths, checkPathHasStringTypeCallback]);

  const hasPathInFormulaValue = useMemo(() => {
    return hasPathInFormula(formulaInput, localValue.paths);
  }, [formulaInput, localValue.paths]);

  const bracketCounter = useMemo(() => {
    return calculateBracketCounter(formulaInput);
  }, [formulaInput]);

  const counterState = useMemo(() => {
    return formulaInput.trim().length === 0;
  }, [formulaInput]);

  const formulaConditions = useMemo(() => {
    return {
      percentage: checkFormulaHasPercentage(formulaInput, localValue.paths),
      root: checkFormulaHasRoot(formulaInput, localValue.paths),
      disableNumbers: checkDisableNumbers(formulaInput, localValue.paths),
      disableSpecialCharacter: checkDisableSpecials(formulaInput, localValue.paths),
      squareAtLast: checkSqAtLast(formulaInput, localValue.paths),
      piAtLast: checkPiAtLast(formulaInput, localValue.paths),
      piSquareAtLast: checkPiSquareAtLast(formulaInput, localValue.paths),
      numberSquareAtLast: checkNumberSquareAtLast(formulaInput, localValue.paths),
      closingBreak: bracketCounter === 0,
      hasPaths: (localValue.paths?.length ?? 0) > 0,
    };
  }, [formulaInput, localValue.paths, bracketCounter]);

  useEffect(() => {
    const hasRoot = checkFormulaHasRoot(formulaInput, localValue.paths);
    setRootAtLast(hasRoot);

    const validation = validateFormula(formulaInput, localValue.paths);
    setFormulaValidation(validation);
  }, [formulaInput, localValue.paths]);

  useEffect(() => {
    const hasCustomModulePath = localValue.paths?.some((p) => p.relation?.location?.name === 'Custom_Modules');
    if (!hasCustomModulePath) return;

    if (cmModuleList.length === 0) {
      listModules({ isActive: true })
        .then((res: unknown) => {
          const modules: SourceRelationOption[] = ((res as ListModulesResponse)?.data?.modules || []).map((m) => ({
            label: m.name || '',
            name: m._id?.toString() || '',
            _id: m._id?.toString() || '',
          }));
          setCmModuleList(modules);
        })
        .catch(() => {});
    }

    if (!cascadePreloadDoneRef.current) {
      cascadePreloadDoneRef.current = true;
      (localValue.paths || []).forEach((path, pathIndex) => {
        const relation = path.relation;
        if (relation?.location?.name !== 'Custom_Modules') return;
        const moduleId = relation.customModule?.name;
        if (!moduleId) return;

        loadModuleTabsForPath(moduleId, (tabs) => {
          setCmTabLists((prev) => ({ ...prev, [pathIndex]: deriveAllTabsFromModule(tabs) }));
        });

        const tabId = relation.customModuleTab?.name;
        if (!tabId) return;

        getLibraryTabDetails(moduleId, tabId)
          .then((res: unknown) => {
            const sections = (res as ModuleTabDetailsResponse)?.data?.tab?.sections ?? [];
            const cacheKey = `${moduleId}:${tabId}`;
            cmTabContentCacheRef.current[cacheKey] = sections;
            setCmSectionLists((prev) => ({ ...prev, [pathIndex]: deriveSectionsFromTab(sections) }));

            const sectionId = relation.customModuleSection?.name;
            if (!sectionId) return;
            const section = sections.find((s) => s._id?.toString() === sectionId);
            if (!section) return;

            setCmSubSectionLists((prev) => ({
              ...prev,
              [pathIndex]: deriveSubSectionsFromSection(section.content || []),
            }));

            const subSectionId = relation.customModuleSubSection?.name;
            if (!subSectionId) return;
            const subSection = (section.content || []).find((sub) => sub._id?.toString() === subSectionId);
            if (!subSection) return;

            setCmControlLists((prev) => ({
              ...prev,
              [pathIndex]: deriveControlsFromSubSection(subSection.content || []),
            }));
          })
          .catch(() => {});
      });
    }
  }, [localValue.paths, cmModuleList, loadModuleTabsForPath]);

  const normalizedValue = useMemo(() => {
    const normalizedPaths = value.paths && Array.isArray(value.paths) && value.paths.length > 0 ? value.paths.map(migratePath) : [];
    return {
      title: value.title ?? '',
      required: !!value.required,
      isVisibleToSupplier: value.isVisibleToSupplier ?? true,
      paths: normalizedPaths,
      formula: value.formula ?? '',
    };
  }, [value]);

  const hasChanges = useMemo(() => {
    const currentValue = {
      ...localValue,
      formula: formulaInput,
    };
    return !isDeepEqual(currentValue, normalizedValue);
  }, [localValue, normalizedValue, formulaInput]);

  const handleSave = useCallback(() => {
    const validation = validateFormula(formulaInput, localValue.paths);
    if (!validation.isValid) {
      if (validation.errorMessage) {
        toasterMsg(t(validation.errorMessage) || validation.errorMessage, 'danger');
      }
      return;
    }

    const valueToSave: CalculationConfigValue = {
      ...localValue,
      formula: formulaInput,
    };
    onChange?.(valueToSave);
    setTimeout(() => {
      isUserEditingRef.current = false;
    }, 100);
  }, [localValue, formulaInput, onChange, t]);

  const updatePath = useCallback(
    (index: number, updates: Partial<CalculationPath>) => {
      isUserEditingRef.current = true;
      setLocalValue((prev) => {
        const newPaths = [...(prev.paths ?? [])];
        const oldPath = newPaths[index];
        if (!oldPath) return prev;

        const updatedPath = { ...oldPath, ...updates };
        newPaths[index] = updatedPath;

        if (updates.pathName && updates.pathName !== oldPath.pathName && oldPath.pathName) {
          const oldPathName = oldPath.pathName.trim();
          const newPathName = updates.pathName.trim();
          if (oldPathName && newPathName && oldPathName !== newPathName) {
            isUserEditingRef.current = true;
            const currentFormula = formulaInput.trim();
            const updatedFormula = currentFormula.replace(new RegExp(oldPathName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), newPathName);
            setFormulaInput(updatedFormula);
            return { ...prev, paths: newPaths, formula: updatedFormula };
          }
        }

        return { ...prev, paths: newPaths };
      });
    },
    [formulaInput],
  );

  const updatePathRelation = useCallback(
    (pathIndex: number, key: keyof SourceRelationValue, optionName: string) => {
      isUserEditingRef.current = true;
      const path = localValue.paths?.[pathIndex];
      if (!path) return;

      const currentRelation: SourceRelationValue = path.relation ?? emptyRelation;
      const pickFrom = (opts: SourceRelationOption[]): SourceRelationOption | null => opts.find((opt) => opt.name === optionName) ?? null;

      let nextRelation: SourceRelationValue = { ...currentRelation };

      if (key === 'location') {
        const selected = pickFrom(dynamicLocationOptions);
        nextRelation = {
          ...emptyRelation,
          location: selected,
        };
        if (selected?._id === 12) (dispatch as unknown as (a: unknown) => unknown)(fetchCustomColumnList(selected.name));
      } else if (key === 'locationType') {
        const source = currentRelation.location?.name === 'Samples' ? sampleTypeFromStore : dynamicBomSelection;
        nextRelation = {
          ...currentRelation,
          locationType: pickFrom(source),
          locationPlacement: null,
          attribute: null,
          output: null,
          subLocation: null,
        };
      } else if (key === 'locationPlacement') {
        const placementOptions = setPlacementForLocation(currentRelation.location?.name, currentRelation.locationType?.name, placement_master);
        const selected = pickFrom(placementOptions);
        nextRelation = {
          ...currentRelation,
          locationPlacement: selected,
          attribute: null,
          output: null,
          subLocation: null,
        };
      } else if (key === 'attribute') {
        const baseAttr: SourceRelationOption[] =
          currentRelation.location?._id === 12
            ? (prouctCustmSectionAttributeList as unknown as Array<{ label: string; name: string }>).map((attributeItem) =>
                buildOption(attributeItem.label, attributeItem.name),
              )
            : setOptionsForAttribute(currentRelation);
        const picked = pickFrom(baseAttr);
        nextRelation = { ...currentRelation, attribute: picked };
        const locName = currentRelation.location?.name;
        const locId = currentRelation.location?._id;
        const typeName = currentRelation.locationType?.name;
        const attrName = picked?.name;
        const outputNullConditions =
          ((['first_item', 'last_item'].includes(attrName || '') &&
            ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Samples', 'Costing_Information'].includes(
              locName || '',
            )) ||
            (['Purchase_Order'].includes(locName || '') && attrName === 'valueType')) ??
          false;
        const outputDateCondition =
          ['first_item', 'last_item'].includes(attrName || '') &&
          (['Design_Details', 'Testing', 'Care/User_Document_Information'].includes(locName || '') ||
            locId === 12 ||
            (['Bill_of_Materials'].includes(locName || '') && typeName !== 'all_item') ||
            (['Bill_of_Materials'].includes(locName || '') && typeName === 'all_item' && ['first_item', 'last_item'].includes(attrName || '')));
        if (outputNullConditions) nextRelation.output = null;
        else if (outputDateCondition) nextRelation.output = buildOption('Date Added', 'date_added');
        else if (locName === 'Purchase_Order' && attrName === 'all_item') nextRelation.output = buildOption('Percentage of Confirmed', 'completion_percentage');
        nextRelation.subLocation = null;
      } else if (key === 'output') {
        const useBom = currentRelation.location?.name === 'Bill_of_Materials';
        const src: SourceRelationOption[] = useBom ? setOptionsForBomOutput(currentRelation) : setOptionsForOutput(currentRelation);
        nextRelation = { ...currentRelation, output: pickFrom(src) };
      } else if (key === 'subLocation') {
        const subLocationOptions = setSubLocationArr(
          currentRelation.location?._id,
          currentRelation.location?.name,
          manualCustomColumns,
          customAttributeDetail,
          sampleTrackingDates,
          subBrandAccess,
        );
        const selected = pickFrom(subLocationOptions);
        nextRelation = {
          ...currentRelation,
          subLocation: selected,
        };
      } else if (key === 'customModule') {
        const selected = cmModuleList.find((m) => m.name === optionName) ?? null;
        nextRelation = {
          ...currentRelation,
          customModule: selected,
          customModuleTab: null,
          customModuleSection: null,
          customModuleSubSection: null,
          customModuleControl: null,
        };
        setCmTabLists((prev) => ({ ...prev, [pathIndex]: [] }));
        setCmSectionLists((prev) => ({ ...prev, [pathIndex]: [] }));
        setCmSubSectionLists((prev) => ({ ...prev, [pathIndex]: [] }));
        setCmControlLists((prev) => ({ ...prev, [pathIndex]: [] }));
        if (optionName) {
          loadModuleTabsForPath(optionName, (tabs) => {
            setCmTabLists((prev) => ({ ...prev, [pathIndex]: deriveAllTabsFromModule(tabs) }));
          });
        }
      } else if (key === 'customModuleTab') {
        const tabs = cmTabLists[pathIndex] || [];
        const selected = tabs.find((t) => t.name === optionName) ?? null;
        nextRelation = {
          ...currentRelation,
          customModuleTab: selected,
          customModuleSection: null,
          customModuleSubSection: null,
          customModuleControl: null,
        };
        setCmSectionLists((prev) => ({ ...prev, [pathIndex]: [] }));
        setCmSubSectionLists((prev) => ({ ...prev, [pathIndex]: [] }));
        setCmControlLists((prev) => ({ ...prev, [pathIndex]: [] }));
        const moduleId = currentRelation.customModule?.name;
        if (moduleId && optionName) {
          loadTabSectionsForPath(moduleId, optionName, (sections) => {
            setCmSectionLists((prev) => ({ ...prev, [pathIndex]: deriveSectionsFromTab(sections) }));
          });
        }
      } else if (key === 'customModuleSection') {
        const sections = cmSectionLists[pathIndex] || [];
        const selected = sections.find((s) => s.name === optionName) ?? null;
        nextRelation = {
          ...currentRelation,
          customModuleSection: selected,
          customModuleSubSection: null,
          customModuleControl: null,
        };
        setCmSubSectionLists((prev) => ({ ...prev, [pathIndex]: [] }));
        setCmControlLists((prev) => ({ ...prev, [pathIndex]: [] }));
        const moduleId = currentRelation.customModule?.name;
        const tabId = currentRelation.customModuleTab?.name;
        if (moduleId && tabId) {
          const cacheKey = `${moduleId}:${tabId}`;
          const cachedSections = cmTabContentCacheRef.current[cacheKey];
          if (cachedSections) {
            const section = cachedSections.find((s) => s._id?.toString() === optionName);
            if (section) {
              setCmSubSectionLists((prev) => ({
                ...prev,
                [pathIndex]: deriveSubSectionsFromSection(section.content || []),
              }));
            }
          }
        }
      } else if (key === 'customModuleSubSection') {
        const subSections = cmSubSectionLists[pathIndex] || [];
        const selected = subSections.find((s) => s.name === optionName) ?? null;
        nextRelation = {
          ...currentRelation,
          customModuleSubSection: selected,
          customModuleControl: null,
        };
        setCmControlLists((prev) => ({ ...prev, [pathIndex]: [] }));
        const moduleId = currentRelation.customModule?.name;
        const tabId = currentRelation.customModuleTab?.name;
        const sectionId = currentRelation.customModuleSection?.name;
        if (moduleId && tabId && sectionId) {
          const cacheKey = `${moduleId}:${tabId}`;
          const cachedSections = cmTabContentCacheRef.current[cacheKey];
          if (cachedSections) {
            const section = cachedSections.find((s) => s._id?.toString() === sectionId);
            const subSection = (section?.content || []).find((sub) => sub._id?.toString() === optionName);
            if (subSection) {
              setCmControlLists((prev) => ({
                ...prev,
                [pathIndex]: deriveControlsFromSubSection(subSection.content || []),
              }));
            }
          }
        }
      } else if (key === 'customModuleControl') {
        const controls = cmControlLists[pathIndex] || [];
        const selected = controls.find((c) => c.name === optionName) ?? null;
        nextRelation = { ...currentRelation, customModuleControl: selected };
      }

      const locN = key === 'location' ? nextRelation.location?.name : currentRelation.location?.name;
      const placeN = key === 'locationPlacement' ? nextRelation.locationPlacement?.name : currentRelation.locationPlacement?.name;
      if (setAttrinuteToValue(locN, placeN)) nextRelation = { ...nextRelation, attribute: buildOption('Value', 'valueType') };

      updatePath(pathIndex, {
        relation: nextRelation,
        breadcrumb: '',
      });
    },
    [
      localValue.paths,
      sampleTypeFromStore,
      dynamicBomSelection,
      placement_master,
      manualCustomColumns,
      customAttributeDetail,
      sampleTrackingDates,
      subBrandAccess,
      updatePath,
      dispatch,
      cmModuleList,
      cmTabLists,
      cmSectionLists,
      cmSubSectionLists,
      cmControlLists,
      loadModuleTabsForPath,
      loadTabSectionsForPath,
    ],
  );

  const handleDeletePath = useCallback(
    (pathIndex: number) => {
      isUserEditingRef.current = true;
      const path = localValue.paths?.[pathIndex];
      if (!path) return;

      const updatedPaths = [...(localValue.paths || [])];
      const pathName = path.pathName?.trim();

      updatedPaths.splice(pathIndex, 1);

      let newFormula = formulaInput.trim();
      if (pathName && newFormula) {
        isUserEditingRef.current = true;
        const escapedPathName = pathName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pathNameRegex = new RegExp(`(^|\\s|[+&×÷(])${escapedPathName}(\\s|[+&×÷)]|$)|^\\s*${escapedPathName}\\s*$`, 'g');
        newFormula = newFormula
          .replace(pathNameRegex, (match, before, after) => {
            if (match.trim() === pathName) {
              return '';
            }
            if (before && after) {
              return before + after;
            }
            return before || after || '';
          })
          .trim();
        newFormula = newFormula.replace(/\s*[+&]\s*[+&]\s*/g, (match) => {
          const hasPlus = match.includes('+');
          const hasAmpersand = match.includes('&');
          if (hasPlus && hasAmpersand) return ' + ';
          return hasPlus ? ' + ' : ' & ';
        });
        newFormula = newFormula.replace(/\s*[+&×÷]\s*[+&×÷]\s*/g, (match) => {
          if (match.includes('+')) return ' + ';
          if (match.includes('&')) return ' & ';
          if (match.includes('×')) return ' × ';
          if (match.includes('÷')) return ' ÷ ';
          return match.trim();
        });
        newFormula = newFormula.replace(/^[+&×÷]\s*|\s*[+&×÷]$/g, '').trim();
        newFormula = newFormula.replace(/\s+/g, ' ');
      }

      setFormulaInput(newFormula);
      if (editingPathIndex === pathIndex) {
        setEditingPathIndex(null);
      } else if (editingPathIndex !== null && editingPathIndex > pathIndex) {
        setEditingPathIndex(editingPathIndex - 1);
      }

      const newValue = {
        ...localValue,
        paths: updatedPaths,
        formula: newFormula,
      };
      setLocalValue(newValue);
    },
    [localValue, formulaInput, editingPathIndex],
  );

  const requestDeletePath = useCallback(
    (pathIndex: number) => {
      if (showDeleteConfirm) return;
      setConfirmDeleteIndex(pathIndex);
      setShowDeleteConfirm(true);
    },
    [showDeleteConfirm],
  );

  const isPathUsedInFormula = useCallback(
    (pathIndex: number): boolean => {
      const path = localValue.paths?.[pathIndex];
      if (!path?.pathName) return false;

      const pathName = path.pathName.trim();
      if (!pathName) return false;

      const currentFormula = formulaInput.trim();
      return currentFormula.includes(pathName);
    },
    [localValue.paths, formulaInput],
  );

  const handleConfirmDelete = useCallback(() => {
    if (confirmDeleteIndex !== null) {
      handleDeletePath(confirmDeleteIndex);
    }
    setShowDeleteConfirm(false);
    setConfirmDeleteIndex(null);
  }, [confirmDeleteIndex, handleDeletePath]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setConfirmDeleteIndex(null);
  }, []);

  const handleClearFormula = useCallback(() => {
    const pathsExist = (localValue.paths?.length ?? 0) > 0;
    if (pathsExist) {
      setShowClearConfirm(true);
    } else {
      isUserEditingRef.current = true;
      setFormulaInput('');
      setLocalValue((prev) => ({ ...prev, formula: '' }));
    }
  }, [localValue.paths]);

  const handleConfirmClear = useCallback(() => {
    isUserEditingRef.current = true;
    setFormulaInput('');
    setLocalValue((prev) => ({ ...prev, paths: [], formula: '' }));
    setEditingPathIndex(null);
    setShowClearConfirm(false);
  }, []);

  const handleCancelClear = useCallback(() => {
    setShowClearConfirm(false);
  }, []);

  const handleAddNewPath = useCallback(() => {
    isUserEditingRef.current = true;
    const currentPaths = Array.isArray(localValue.paths) ? localValue.paths : [];
    const nextPathNumber = currentPaths.length + 1;
    const newPath = { ...emptyPath, pathName: `Path ${nextPathNumber}` };

    const newValue = {
      ...localValue,
      paths: [...currentPaths, newPath],
    };
    setLocalValue(newValue);
  }, [localValue]);

  const handleCreatePath = useCallback(
    (pathIndex: number) => {
      isUserEditingRef.current = true;
      const path = localValue.paths?.[pathIndex];
      if (!path || !path.pathName || !path.relation?.location) {
        return;
      }

      const pathIsComplete = isPathComplete(path);
      if (!pathIsComplete) {
        return;
      }

      const updatedPaths = [...(localValue.paths || [])];
      const breadcrumb = buildBreadcrumb(path);
      updatedPaths[pathIndex] = { ...path, breadcrumb };

      const pathName = path.pathName.trim();
      if (!pathName) {
        return;
      }

      const isStringType = checkPathHasStringTypeCallback({ ...path, breadcrumb });
      const currentFormula = formulaInput.trim();
      const pathAlreadyInFormula = currentFormula.includes(pathName);

      const hasAnyStringPathInFormula =
        localValue.paths?.some((p) => {
          if (!p.pathName || p.pathName === pathName) return false;
          return currentFormula.includes(p.pathName) && checkPathHasStringTypeCallback(p);
        }) ?? false;

      let newFormula = currentFormula;
      if (!pathAlreadyInFormula) {
        if (isStringType || hasAnyStringPathInFormula) {
          const separator = '&';
          newFormula = currentFormula ? `${currentFormula}${separator}${pathName}` : pathName;
        } else {
          const separator = '+';
          newFormula = currentFormula ? `${currentFormula}${separator}${pathName}` : pathName;
        }
      }

      if (isStringType || hasAnyStringPathInFormula) {
        newFormula = newFormula
          .replace(/\+/g, '&')
          .replace(/-/g, '&')
          .replace(/×/g, '&')
          .replace(/÷/g, '&')
          .replace(/\s*&\s*&/g, ' & ')
          .replace(/^&\s*|\s*&$/g, '')
          .trim();
      }

      setFormulaInput(newFormula);
      setEditingPathIndex(null);

      const newValue = {
        ...localValue,
        paths: updatedPaths,
        formula: newFormula,
      };
      setLocalValue(newValue);
    },
    [localValue, formulaInput, checkPathHasStringTypeCallback],
  );

  useEffect(() => {
    if (!hasChanges || !validateFormula(formulaInput, localValue.paths).isValid) return;
    onChange?.({ ...localValue, formula: formulaInput });
  }, [hasChanges, formulaInput, localValue, onChange]);

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && (
        <div className="mb-2">
          <NewInput
            type="text"
            placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.CALCULATION_NAME_PLACEHOLDER')}
            value={localValue.title}
            onChange={(e) => updateCalculationName(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="calculation"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      <div className="mb-2 mt-2 d-flex align-items-center justify-content-between cm-checkbox">
        <div className="d-flex align-items-center">
          <label className="m-0 mr-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}</label>
          {!localValue.title?.trim() ? (
            <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
              <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
            </CustomTooltip>
          ) : (
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          )}
          </div>
        <div className="d-flex align-items-center cm-checkbox">
          <NewCheckbox className="scale12 mr-2" checked={!!localValue.isVisibleToSupplier} onChange={toggleSupplierVisible} disabled={disabled} aria-label={t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.VISIBLE_SUPLIER')} />
          <label className="cursor-pointer m-0 d-flex align-items-center" onClick={disabled ? undefined : toggleSupplierVisible}>
            <span>{t('PRODUCTCP.MODALS.ADD_EDIT_COLUMNS.VISIBLE_SUPLIER')}</span>
          </label>
        </div>
      </div>

      <div className="card">
        <div className="p-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="m-0 font-weight-500">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.CALCULATION_NAME')}</span>
            {hasChanges && (
              <button
                type="button"
                onClick={handleSave}
                className="bombiix-btn btn-blue d-inline-flex align-items-center"
                aria-label={t('ACTIONS.SAVE') || 'Save'}
                disabled={disabled}
              >
                <SaveIcon size={24} className="text-white align-text-bottom" />
              </button>
            )}
          </div>
          {!showWidthRow && !hideTitle && (
            <div className="mb-2">
              <NewInput
                type="text"
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.CALCULATION_NAME_PLACEHOLDER')}
                value={localValue.title}
                onChange={(e) => updateCalculationName(e.target.value)}
                disabled={disabled}
              />
            </div>
          )}
          <div className="mb-3 cm-calculation-formula-container">
            <div className="cm-calculation-formula-display-area">
              <FormulaDisplay formulaInput={formulaInput} paths={localValue.paths ?? []} disabled={disabled} />
            </div>
            <FormulaButtons
              setFormulaInput={(updater) => {
                isUserEditingRef.current = true;
                setFormulaInput(updater);
              }}
              disabled={disabled}
              formulaConditions={formulaConditions}
              hasStringTypePath={hasStringTypePathValue}
              hasPathInFormula={hasPathInFormulaValue}
              counterState={counterState}
              onClearFormula={handleClearFormula}
            />
          </div>
          {(formulaValidation.errorMessage || rootAtLast) && (
            <div className="mt-2">
              {formulaValidation.errorMessage && (
                <span className="error-message text-center d-block">{t(formulaValidation.errorMessage) || formulaValidation.errorMessage}</span>
              )}
              {rootAtLast && (
                <span className="error-message text-center d-block">
                  {t('CUSTOM_LIBRARY.CALCULATION_TYPE.ROOT_AT_LAST') || 'Root cannot be at the end of formula'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-2">
        <div className="cm-calculation-paths-container">
          {localValue.paths?.map((path, index) => (
            <PathConfig
              key={index}
              path={path}
              index={index}
              disabled={disabled}
              editingPathIndex={editingPathIndex}
              setEditingPathIndex={setEditingPathIndex}
              updatePath={updatePath}
              updatePathRelation={updatePathRelation}
              handleCreatePath={handleCreatePath}
              requestDeletePath={requestDeletePath}
              dynamicBomSelection={dynamicBomSelection}
              sampleTypeFromStore={sampleTypeFromStore}
              locationOptions={dynamicLocationOptions}
              {...(placement_master && { placement_master })}
              {...(manualCustomColumns && { manualCustomColumns })}
              {...(customAttributeDetail && { customAttributeDetail })}
              {...(sampleTrackingDates && { sampleTrackingDates })}
              {...(subBrandAccess !== undefined && { subBrandAccess })}
              cmModuleList={cmModuleList}
              cmTabList={cmTabLists[index] || []}
              cmSectionList={cmSectionLists[index] || []}
              cmSubSectionList={cmSubSectionLists[index] || []}
              cmControlList={cmControlLists[index] || []}
            />
          ))}
        </div>
        <div className="d-flex justify-content-center mt-3">
          <button type="button" className="bombiix-btn btn-blue" onClick={handleAddNewPath} disabled={disabled}>
            {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.ADD_PATH') || 'Add Path'}
          </button>
        </div>
      </div>
      <ConfirmPopup
        onOpen={showDeleteConfirm}
        title={t('COMMON.DELETE') || 'Delete Path'}
        message={
          confirmDeleteIndex !== null && isPathUsedInFormula(confirmDeleteIndex)
            ? t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.DELETE_PATH_WITH_FORMULA_WARNING') ||
              'This path is used in the formula. Are you sure you want to delete it? It will also be removed from the formula.'
            : t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.DELETE_PATH_CONFIRM') || 'Are you sure you want to delete this path?'
        }
        toggle={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <ConfirmPopup
        onOpen={showClearConfirm}
        title={t('COMMON.CLEAR')}
        message={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.CLEAR_FORMULA_WITH_PATHS_WARNING')}
        toggle={handleCancelClear}
        onConfirm={handleConfirmClear}
      />
    </div>
  );
};

export default memo(CalculationConfig);
